import { useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardFacePlate, type BusinessCardData } from '@/components/BusinessCard';
import { cardSize, type Orientation } from '@/lib/cardFormat';
import { downloadBlob, rasterize, slugify } from '@/lib/exportImage';
import { useToast } from '@/hooks/useToast';

/** Output pixels per CSS pixel. At 3x a 3.5-inch card clears 500 DPI. */
const EXPORT_SCALE = 3;

interface CardDownloadProps extends BusinessCardData {
  orientation: Orientation;
  /** Name the saved files are keyed to. */
  name: string;
}

type Side = 'front' | 'back';

/**
 * Saves either face of the card as a PNG.
 *
 * The visible card is a stack of 3D layers — one face is always rotated away
 * and faded out, which is exactly the wrong thing to photograph. So each face
 * is also rendered flat on an off-screen stage at full print size, and it is
 * those plates the exporter rasterizes. They are the same `CardFacePlate` the
 * viewer draws, so the file can't disagree with what's on screen.
 */
export function CardDownload({ name, ...card }: CardDownloadProps) {
  const { orientation, theme } = card;
  const { width, height } = cardSize(orientation);

  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<Side | null>(null);
  const { toast } = useToast();

  const save = async (side: Side) => {
    const node = (side === 'front' ? frontRef : backRef).current;
    if (!node || busy) return;

    setBusy(side);
    try {
      const blob = await rasterize(node, {
        width,
        height,
        scale: EXPORT_SCALE,
        fonts: [theme.bodyFont, theme.titleFont],
      });
      downloadBlob(blob, `${slugify(name)}-card-${orientation}-${side}.png`);
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error instanceof Error
          ? error.message
          : 'The card could not be saved as an image.',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {(['front', 'back'] as Side[]).map((side) => (
          <Button
            key={side}
            variant="outline"
            onClick={() => save(side)}
            disabled={busy !== null}
            className="border-white/15 bg-transparent capitalize text-slate-200 hover:bg-white/10 hover:text-white"
          >
            {busy === side ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {side}
          </Button>
        ))}
      </div>
      <p className="text-[11px] text-slate-600">
        PNG at {width * EXPORT_SCALE} × {height * EXPORT_SCALE}, with a transparent
        area outside the rounded corners.
      </p>

      {/* The export stage. Kept in the layout — and merely moved out of sight —
          because the exporter reads each element's *computed* style, which a
          `display: none` subtree simply doesn't have.

          Each face is a full-size card, so the stage has to be sealed off from
          the page's scroll area or it drags the document's height down with it.
          `position: fixed` is not enough on its own: an ancestor with a filter,
          transform or backdrop-filter becomes the containing block for fixed
          descendants, and this panel has `backdrop-blur`. So the stage lives
          inside a zero-sized clipping box instead — `overflow: hidden` keeps
          its contents out of every ancestor's scrollable overflow, wherever the
          box ends up anchored. Clipping is purely visual: layout, computed
          styles and image loading all still happen, which is everything the
          exporter needs. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 h-0 w-0 select-none overflow-hidden"
      >
        <div ref={frontRef} style={{ width, height }}>
          <CardFacePlate {...card} side="front" className="h-full w-full" />
        </div>
        <div ref={backRef} style={{ width, height }}>
          <CardFacePlate {...card} side="back" className="h-full w-full" />
        </div>
      </div>
    </>
  );
}
